import React from 'react';
import { Button } from '@/components/ui/button';
import { PDFPageSelectionModal } from './PDFPageSelectionModal';
import { FloorPlanNameModal } from './FloorPlanNameModal';
import { useToast } from '@/components/ui/use-toast';
import { Upload } from 'lucide-react';
// Use lazy loading for PDF.js with proper worker configuration
import { configurePDFWorker } from '@/utils/pdfWorkerConfig';
import { useAuth } from '@/components/auth/AuthProvider';
import { useAuth as useClerkAuth, useOrganization } from '@clerk/clerk-react';
import { uploadFloorPlan, createFloorPlanRecord } from '@/services/floorPlanService';
import { processPDFPage, DEFAULT_SCALE } from '@/utils/pdfUtils';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { PDFPage } from '@/types/floorPlan';
// Removed direct import - PDF.js will be loaded lazily when needed
// import '@/utils/pdfWorkerConfig';
import { logger } from '@/lib/logger';

interface FloorPlanUploaderProps {
  projectId: string;
  onUploadComplete: () => void;
  showFullUI?: boolean;
}

export function FloorPlanUploader({
  projectId,
  onUploadComplete,
  showFullUI = false,
}: FloorPlanUploaderProps) {
  // Check if using Clerk
  const isUsingClerk = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  const clerkAuth = useClerkAuth();
  const { organization } = useOrganization();
  const supabaseAuth = useAuth();

  // Get user ID based on auth type
  // For Clerk, use the organization ID as the user ID for file paths
  const userId = isUsingClerk
    ? organization?.id || clerkAuth?.userId || 'unknown'
    : supabaseAuth.user?.id || 'unknown';

  const [isPDFModalOpen, setIsPDFModalOpen] = React.useState(false);
  const [isNameModalOpen, setIsNameModalOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadingMessage, setLoadingMessage] = React.useState('');
  const [progress, setProgress] = React.useState(0);
  const [pdfPages, setPDFPages] = React.useState<PDFPage[]>([]);
  const [currentFile, setCurrentFile] = React.useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCurrentFile(file);

    if (file.type === 'application/pdf') {
      await handlePDFUpload(file);
    } else if (file.type.startsWith('image/')) {
      setIsNameModalOpen(true);
    } else {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a PDF or image file.',
        variant: 'destructive',
      });
    }
  };

  const handlePDFUpload = async (file: File) => {
    setIsLoading(true);
    setProgress(0);
    setLoadingMessage('Loading PDF library...');

    logger.debug('Starting PDF upload:', file.name);

    try {
      // Lazy load and configure PDF.js library with worker
      const pdfjsLib = await configurePDFWorker();
      logger.debug('PDF.js loaded and configured successfully');
      logger.debug('PDF.js version:', pdfjsLib.version);
      logger.debug('Worker configured:', !!pdfjsLib.GlobalWorkerOptions.workerSrc);

      setLoadingMessage('Processing PDF pages...');
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      logger.debug('Creating PDF document...');
      const loadingTask = pdfjsLib.getDocument({
        data: uint8Array,
        cMapUrl: '/pdfjs-dist/cmaps/',
        cMapPacked: true,
        // verbosity: pdfjsLib.VerbosityLevel.INFOS // TODO: Add verbose logging when types are fixed
      });

      loadingTask.onProgress = (progressData: any) => {
        logger.debug(`PDF loading: ${progressData.loaded} / ${progressData.total} bytes`);
      };

      const pdf = await loadingTask.promise;
      logger.debug(`PDF loaded: ${pdf.numPages} pages`);

      const totalPages = pdf.numPages;
      const pages: PDFPage[] = [];

      for (let i = 1; i <= totalPages; i++) {
        try {
          logger.log(`Rendering page ${i}/${totalPages}...`);
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 0.5 });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          canvas.width = viewport.width;
          canvas.height = viewport.height;

          if (context) {
            await page.render({
              canvasContext: context,
              viewport: viewport,
              background: 'white',
            }).promise;
          }

          pages.push({
            pageNumber: i,
            thumbnail: canvas.toDataURL(),
            selected: false,
            name: `${file.name.replace('.pdf', '')} - Page ${i}`,
          });

          setProgress((i / totalPages) * 100);
        } catch (pageError) {
          logger.error(`Error rendering page ${i}:`, pageError);
          toast({
            title: 'Page rendering error',
            description: `Failed to render page ${i}. Continuing with other pages.`,
            variant: 'destructive',
          });
        }
      }

      if (pages.length === 0) {
        throw new Error('No pages could be rendered from the PDF');
      }

      setPDFPages(pages);
      setIsPDFModalOpen(true);
    } catch (error) {
      logger.error('Detailed PDF processing error:', error);

      let errorMessage = 'There was an error processing your PDF file. Please try again.';
      if (error instanceof Error) {
        if (error.message.includes('worker')) {
          errorMessage = 'PDF worker failed to initialize. Please refresh the page and try again.';
        } else if (error.message.includes('Invalid PDF')) {
          errorMessage = 'The file appears to be corrupted or is not a valid PDF.';
        } else if (error.message.includes('No pages')) {
          errorMessage = 'Could not extract any pages from the PDF.';
        }
      }

      toast({
        title: 'Error processing PDF',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const handlePageSelect = (pageNumber: number) => {
    setPDFPages(pages =>
      pages.map(page =>
        page.pageNumber === pageNumber ? { ...page, selected: !page.selected } : page,
      ),
    );
  };

  const handlePageNameChange = (pageNumber: number, name: string) => {
    setPDFPages(pages =>
      pages.map(page => (page.pageNumber === pageNumber ? { ...page, name } : page)),
    );
  };

  const handlePDFContinue = async () => {
    if (!userId || userId === 'unknown') {
      toast({
        title: 'Error',
        description: 'You must be logged in to upload floor plans.',
        variant: 'destructive',
      });
      return;
    }

    setIsPDFModalOpen(false);
    setIsLoading(true);
    setProgress(0);

    const selectedPages = pdfPages.filter(p => p.selected);
    let processed = 0;

    try {
      // Lazy load and configure PDF.js library if needed
      const pdfjsLib = await configurePDFWorker();

      // Load the PDF once for all pages
      setLoadingMessage('Processing selected pages...');
      const arrayBuffer = await currentFile!.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({
        data: arrayBuffer,
        cMapUrl: '/pdfjs-dist/cmaps/',
        cMapPacked: true,
      }).promise;

      for (const page of selectedPages) {
        try {
          setLoadingMessage(`Processing page ${page.pageNumber} of ${selectedPages.length}...`);

          // Get and process the page with auto-cropping
          const pdfPage = await pdf.getPage(page.pageNumber);
          const processedCanvas = await processPDFPage(pdfPage, DEFAULT_SCALE); // Higher scale for final output (~200 DPI)

          // Convert to PNG blob
          const blob = await new Promise<Blob>(resolve => {
            processedCanvas.toBlob(blob => {
              if (blob) resolve(blob);
            }, 'image/png');
          });

          // Create a File object from the blob
          const file = new File([blob], `${page.name}.png`, { type: 'image/png' });

          setLoadingMessage(`Uploading page ${page.pageNumber}...`);

          // Upload floor plan
          const {
            filePath,
            fileType,
            error: uploadError,
          } = await uploadFloorPlan(file, projectId, userId, organization?.id);

          if (uploadError) {
            logger.error('Error uploading file:', uploadError);
            toast({
              title: 'Upload failed',
              description: `Failed to upload page ${page.pageNumber}`,
              variant: 'destructive',
            });
            continue;
          }

          // Create floor plan record
          const { data: floorPlanData, error: recordError } = await createFloorPlanRecord(
            projectId,
            page.name,
            filePath,
            fileType,
            isUsingClerk ? clerkAuth.userId : null,
          );

          if (recordError || !floorPlanData) {
            logger.error('Error creating floor plan record:', recordError);
            continue;
          }

          processed++;
          setProgress((processed / selectedPages.length) * 100);
        } catch (pageError) {
          logger.error(`Error processing page ${page.pageNumber}:`, pageError);
        }
      }

      if (processed > 0) {
        toast({
          title: 'Success',
          description: `Successfully uploaded ${processed} floor plan${processed > 1 ? 's' : ''}.`,
        });
        onUploadComplete();
      } else {
        toast({
          title: 'Error',
          description: 'No pages were successfully uploaded. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      logger.error('Error uploading floor plans:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an error uploading your floor plans. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
      setPDFPages([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImageUpload = async (name: string) => {
    if (!currentFile || !name.trim() || !userId || userId === 'unknown') return;

    setIsNameModalOpen(false);
    setIsLoading(true);
    setLoadingMessage('Uploading floor plan...');

    try {
      const {
        filePath,
        fileType,
        error: uploadError,
      } = await uploadFloorPlan(currentFile, projectId, userId, organization?.id);

      if (uploadError) {
        throw uploadError;
      }

      const { data: floorPlanData, error: recordError } = await createFloorPlanRecord(
        projectId,
        name.trim(),
        filePath,
        fileType,
        isUsingClerk ? clerkAuth.userId : null,
      );

      if (recordError || !floorPlanData) {
        throw recordError;
      }

      toast({
        title: 'Success',
        description: 'Floor plan uploaded successfully.',
      });

      onUploadComplete();
    } catch (error) {
      logger.error('Error uploading floor plan:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an error uploading your floor plan. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*,application/pdf"
        className="hidden"
      />

      {showFullUI ? (
        <div
          className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="h-12 w-12 text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-900">Upload Floor Plan</p>
          <p className="text-sm text-gray-500 mt-1">Click to upload or drag and drop</p>
          <p className="text-xs text-gray-400 mt-2">PDF or Image files</p>
        </div>
      ) : (
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading}
          className="flex items-center gap-2"
        >
          <Upload className="h-4 w-4" />
          Upload Floor Plan
        </Button>
      )}

      {isLoading && (
        <LoadingOverlay message={loadingMessage} progress={progress > 0 ? progress : undefined} />
      )}

      <PDFPageSelectionModal
        isOpen={isPDFModalOpen}
        onClose={() => {
          setIsPDFModalOpen(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }}
        pages={pdfPages}
        onPageSelect={handlePageSelect}
        onPageNameChange={handlePageNameChange}
        onContinue={handlePDFContinue}
        isLoading={isLoading}
        progress={progress}
        fileName={currentFile?.name.replace('.pdf', '') || ''}
      />

      <FloorPlanNameModal
        isOpen={isNameModalOpen}
        onClose={() => {
          setIsNameModalOpen(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }}
        onSubmit={handleImageUpload}
        defaultName={currentFile?.name.split('.')[0] || ''}
      />
    </>
  );
}
